<template>
    <div class="rc-base-input">
        <div class="input-wrapper">
            <slot name="label">
                <label
                    :for="input_id"
                    class="input-label"
                    :class="{ 'required': requiredBool, 'underlined': !outlined }"
                >
                    <span>
                        {{ label }}
                    </span>
                    <span
                        v-if="requiredBool"
                        class="required-indicator"
                        aria-hidden="true"
                    >*</span>
                </label>
            </slot>

            <div class="input-container" :class="{ 'has-icon': icon }">
                <v-icon
                    v-if="icon"
                    class="input-icon"
                    :class="{ 'input-icon-focused': is_focused }"
                    size="20"
                >{{ icon }}</v-icon>
                <input
                    :id="input_id"
                    :type="inputType"
                    :value="local_value"
                    :disabled="disabled"
                    :readonly="readonly"
                    :required="requiredBool"
                    :autocomplete="calculatedAutocomplete"
                    v-bind="{
                        ...(placeholder ? { placeholder } : {}),
                        ...(describedByIds?.length ? { 'aria-describedby': describedByIds } : {}),
                        ...(hasError ? { 'aria-invalid': 'true' } : {}),
                    }"
                    :inputmode="inputMode"
                    :pattern="pattern"
                    :maxlength="maxlength"
                    class="form-input"
                    data-1p-ignore
                    :class="[inputClasses, { 'has-prepend-icon': icon }]"
                    @input="onInput"
                    @blur="onBlur"
                    @focus="onFocus"
                    @keydown="onKeydown"
                    ref="input"
                />

                <div
                    v-if="$slots['append-outer']"
                    class="append-outer-slot"
                >
                    <slot name="append-outer"></slot>
                </div>
            </div>

            <div
                v-if="hasError && !(is_focused && (inputHint || $slots.hint))"
                class="error-message"
                :id="`${input_id}-error`"
                role="alert"
            >
                {{ displayErrorMessage }}
            </div>

            <div
                v-if="(inputHint || $slots.hint) && (!hasError || is_focused)"
                class="hint-text"
                :id="`${input_id}-hint`"
            >
                <slot name="hint">
                    {{ inputHint }}
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * rc-base-input
 *
 * emits 'input' event with new value on change
 * emits 'validated' event with boolean validity on validation (for tel, email, birthday, etc. types)
 * emits 'focus', 'blur', and 'keydown' events as well
 *
 * This is a custom, accessibility-focused form input with no external UI dependencies.
 * Supports multiple types with built-in validation, autocomplete, and input mode.
 * Includes built-in hint for birthday input type.
 * Is optimized for AA accessibility compliance.
 *
 * It was originally built for use in public forms where accessibility couldn't be achieved via Vuetify 2 wrappers.
 * However it can also be used in the redchirp app itself.
 * NOTE: this does look different in some (pretty subtle) ways from the Vuetify 2 inputs it replaces.
 * When using this, at least ensure that all other inputs in the same form are also this component, to maintain visual consistency.
 */

import E164 from '@/js/E164.js';
import Validate from '@/js/validate.js';

export default {
    name: 'RcBaseInput',

    props: {
        value: {
            type: [String, Number, Array],
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'text',
            validator: value => ['text', 'email', 'official-email', 'tel', 'birthday', 'us-or-ca-tax-id'].includes(value)
        },
        /** * Corresponds to the native inputMode attribute
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/inputmode
         */
        mode: {
            type: String,
            default: 'text'
        },
        placeholder: {
            type: String,
            default: ''
        },
        hint: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: true
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        /**
         * Can be bool or the string optional/required in the case of custom forms
         */
        required: {
            type: [Boolean, String],
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: ''
        },

        /**
         * Corresponds to the native autocomplete attribute
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
         * 
         * Set to null or false to omit the autocomplete attribute entirely.
         * Autocomplete is already handled for text, email, tel, and birthday types if not set here.
         */
        autocomplete: {
            type: [String, Boolean],
            default: null
        },
        pattern: {
            type: String,
            default: null
        },
        maxlength: {
            type: [String, Number],
            default: null
        },

        /**
         * Icon to display on the left side of the input
         * Uses Material Design Icons (mdi-*)
         */
        icon: {
            type: String,
            default: null
        },

        /**
         * For birthday type, the minimum age required.
         */
        minAge: {
            type: Number,
            default: null
        },
    },

    data() {
        return {
            input_id: `input-${Math.random().toString(36).substr(2, 9)}`,
            is_focused: false,
            local_value: this.value,
            input_touched: false,
            input_valid: true,
            validation_error_message: '',
            raw_digits: '',
            screen_reader_announcement: ''
        }
    },

    computed: {        
        requiredBool() {
            // can sometimes come in as "optional"
            return this.required === true || this.required === 'required';
        },

        calculatedAutocomplete() {
            if (this.autocomplete === false) return null; // can pass false to forgo attribute on DOM el entirely
            if (this.autocomplete) return this.autocomplete; // if explicitly provided, that wins out

            if (['email', 'official-email'].includes(this.type)) return 'email';
            if (this.type === 'birthday') return 'bday';
            if (this.type === 'tel') return 'tel';

            return null;
        },

        inputType() {
            if (this.type === 'birthday') return 'tel'
            return this.type
        },

        inputMode() {
            if (this.type === 'tel') return 'tel'
            if (this.type === 'birthday') return 'numeric'
            if (['email', 'official-email'].includes(this.type)) return 'email'
            return this.mode
        },

        inputHint() {
            if (this.hint) return this.hint; // if explicitly provided, that wins out

            if (this.type === 'birthday') {
                return 'MM/DD/YYYY -- use a leading zero (e.g. 01 for January). Slashes will be added automatically'
            }

            return '';
        },

        hasError() {
            return this.error || (this.input_touched && !this.input_valid) || (this.validation_error_message && this.validation_error_message.length > 0)
        },

        displayErrorMessage() {
            return this.input_touched && !this.input_valid && this.validation_error_message ? this.validation_error_message : ''
        },

        inputClasses() {
            return {
                'input-focused': this.is_focused,
                'input-error': this.hasError,
                'input-disabled': this.disabled,
                'input-readonly': this.readonly,
                'input-outlined': this.outlined,
                'input-underlined': !this.outlined
            }
        },

        describedByIds() {
            const ids = []
            const has_hint_content = this.hint || this.$slots.hint
            
            if (has_hint_content && (!this.hasError || this.is_focused)) {
                ids.push(`${this.input_id}-hint`)
            }
            if (this.hasError && !(this.is_focused && has_hint_content)) {
                ids.push(`${this.input_id}-error`)
            }
            return ids.join(' ')
        },
    },

    methods: {
        onInput(event) {
            const value = event.target.value

            if (this.type === 'tel') {
                this.handlePhoneInput(value)
            } else if (this.type === 'email') {
                this.handleEmailInput(value)
            } else if (this.type === 'official-email') {
                this.handleOfficialEmailInput(value)
            } else if (this.type === 'birthday') {
                this.handleBirthdayInput(value)
            } else if (this.type === 'us-or-ca-tax-id') {
                this.handleTaxIdInput(value)
            } else {
                this.handleTextInput(value)
            }
        },

        onFocus(event) {
            this.focus()
            this.$emit('focus', event)
        },

        onBlur(event) {
            this.is_focused = false

            if (this.type === 'tel') {
                this.handlePhoneBlur()
            } else if (this.type === 'email') {
                this.handleEmailBlur(event.target.value)
            } else if (this.type === 'official-email') {
                this.handleOfficialEmailBlur(event.target.value)
            } else if (this.type === 'birthday') {
                this.handleBirthdayBlur()
            } else if (this.type === 'us-or-ca-tax-id') {
                this.handleTaxIdBlur()
            } else if (this.type === 'text') {
                this.handleTextBlur()
            }

            this.$emit('blur', event)
        },

        onKeydown(event) {
            this.$emit('keydown', event)
        },

        getDigitsOnly(value) {
            return ('' + value).replace(/\D/g, '');
        },

        maskDigits(digits) {
            return this.$rc.maskPhoneNumber(digits);
        },

        // check for anything other than digits, spaces, parentheses, hyphens, plus
        hasInvalidCharacters(value) {
            if (!value) return false;
            return /[^0-9\s()\-+]/.test(value);
        },

        handlePhoneInput(value) {
            if (this.hasInvalidCharacters(value)) {
                this.input_touched = true;
                this.input_valid = false;
                this.validation_error_message = 'Invalid phone number';
                this.$emit('validated', this.input_valid);
                return;
            }

            const digits = this.getDigitsOnly(value);
            this.raw_digits = digits;

            if (digits.length <= 11) {
                this.local_value = digits ? this.maskDigits(digits) : '';
            } else {
                this.local_value = value;
            }
            this.$emit('input', this.local_value);

            this.validatePhoneDigits();
        },

        handlePhoneBlur() {
            this.input_touched = true;

            if (this.hasInvalidCharacters(this.local_value)) {
                this.input_valid = false;
                this.validation_error_message = 'Invalid phone number';
                this.$emit('validated', this.input_valid);
                return;
            }

            this.validatePhoneDigits();
        },

        validatePhoneDigits() {
            const north_america_message = 'Oops! Please enter a valid North American phone number (e.g. 1 (555) 123-4567).';

            if (!this.raw_digits) {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label || 'Phone number'} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            if (this.raw_digits.length < 10) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            // ITU-T E.164 standard limits international phone numbers to 15 digits max
            if (this.raw_digits.length > 15) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            if (this.raw_digits.length > 11) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? north_america_message : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            // if 11 digits, must start with 1 (North America country code)
            if (this.raw_digits.length === 11 && this.raw_digits.charAt(0) !== '1') {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? north_america_message : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            let usableDigits = this.raw_digits;
            if (usableDigits.charAt(0) == '1') {
                usableDigits = usableDigits.substring(1);
            }

            // after removing country code, need exactly 10 digits for complete number
            if (usableDigits.length !== 10) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            try {
                this.input_valid = !!E164.isUSPhoneNumber(this.raw_digits);
                this.validation_error_message = (this.input_touched && !this.input_valid) ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
            } catch (e) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
            }

            this.$emit('validated', this.input_valid);
        },

        validateEmail(email) {
            if (!email) {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label || 'Email'} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            this.input_valid = Validate.isBlankOrEmailAddress(email);
            this.validation_error_message = (this.input_touched && !this.input_valid) ? `Please enter a valid ${this.label || 'email address'} (e.g. user@example.com)` : '';
            this.$emit('validated', this.input_valid);
        },

        handleEmailInput(value) {
            this.local_value = value;
            this.$emit('input', value);
            this.validateEmail(value);
        },

        handleEmailBlur(value = this.local_value) {
            this.input_touched = true;
            this.validateEmail(value);
        },

        validateOfficialEmail(email) {
            if (!email) {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label || 'Email'} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            if (!Validate.isEmailAddress(email)) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? `Please enter a valid ${this.label || 'email address'} (e.g. user@example.com)` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            if (!Validate.isValidOfficialEmail(email)) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? 'Please enter an individual business email address (not a group or generic email). The Campaign Registry requires a personal point of contact. For example, john_doe@yourcompany.com instead of john_doe@gmail.com or support@yourcompany.com.' : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            this.input_valid = true;
            this.validation_error_message = '';
            this.$emit('validated', this.input_valid);
        },

        handleOfficialEmailInput(value) {
            this.local_value = value;
            this.$emit('input', value);
            this.validateOfficialEmail(value);
        },

        handleOfficialEmailBlur(value = this.local_value) {
            this.input_touched = true;
            this.validateOfficialEmail(value);
        },

        validateText(value) {
            if (!value || value.trim() === '') {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            this.input_valid = true;
            this.validation_error_message = '';
            this.$emit('validated', this.input_valid);
        },

        handleTextInput(value) {
            this.local_value = value;
            this.$emit('input', value);
            this.validateText(value);
        },

        handleTextBlur() {
            this.input_touched = true;
            this.validateText(this.local_value);
        },

        calculateAge(dateString) {
            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
                return null; // Invalid format
            }

            const [month, day, year] = dateString.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            // Check if the parsed date is valid (e.g., handles 02/30/2000)
            if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
                return null;
            }

            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDifference = today.getMonth() - birthDate.getMonth();

            if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            return age;
        },

        validateBirthday(value) {
            // 1. Handle empty value
            if (!value) {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label || 'Birthday'} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            // 2. Validate date format (MM/DD/YYYY)
            if (!Validate.isValidDateFormat(value)) {
                this.input_valid = false;
                this.validation_error_message = (this.input_touched) ? `Please enter a valid ${this.label || 'birthday'} (MM/DD/YYYY)` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            // 3. Check for minimum age if the prop is provided
            if (this.minAge !== null && this.minAge > 0) {
                const age = this.calculateAge(value);

                // This handles logically impossible dates like 02/30/2000 that pass the format regex
                if (age === null) {
                    this.input_valid = false;
                    this.validation_error_message = (this.input_touched) ? 'Please enter a valid date.' : '';
                    this.$emit('validated', this.input_valid);
                    return;
                }

                if (age < this.minAge) {
                    this.input_valid = false;
                    this.validation_error_message = (this.input_touched) ? `You must be at least ${this.minAge} years old.` : '';
                    this.$emit('validated', this.input_valid);
                    return;
                }
            }

            // 4. If all checks pass, the value is valid
            this.input_valid = true;
            this.validation_error_message = '';
            this.$emit('validated', this.input_valid);
        },

        handleBirthdayInput(value) {
            this.local_value = this.$rc.maskMMDDYYYY(value);
            this.$emit('input', this.local_value);
            this.validateBirthday(this.local_value);
        },

        handleBirthdayBlur() {
            this.input_touched = true;
            this.validateBirthday(this.local_value);
        },

        validateTaxId(value) {
            if (!value || value.trim() === '') {
                this.input_valid = !this.requiredBool;
                this.validation_error_message = (this.input_touched && this.requiredBool) ? `${this.label || 'Tax ID'} is required` : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            if (!Validate.isValidTaxId(value)) {
                this.input_valid = false;
                this.validation_error_message = this.input_touched ? 'Please enter a valid 9-digit Tax ID (US: EIN, Canada: BN 9)' : '';
                this.$emit('validated', this.input_valid);
                return;
            }

            this.input_valid = true;
            this.validation_error_message = '';
            this.$emit('validated', this.input_valid);
        },

        handleTaxIdInput(value) {
            this.local_value = value;
            this.$emit('input', value);
            this.validateTaxId(value);
        },

        handleTaxIdBlur() {
            this.input_touched = true;
            this.validateTaxId(this.local_value);
        },

        focus() {
            this.is_focused = true
            this.$refs.input?.focus()
        },

        blur() {
            this.is_focused = false
            this.$refs.input?.blur()
        },
    },

    watch: {
        value(newValue) {
            if (this.type === 'tel') {
                const digits = this.getDigitsOnly(newValue);
                this.raw_digits = digits;

                if (digits.length <= 11) {
                    this.local_value = digits ? this.maskDigits(digits) : '';
                } else {
                    this.local_value = newValue;
                }
                this.validatePhoneDigits();
            } else if (this.type === 'email') {
                this.local_value = newValue;
                this.validateEmail(newValue);
            } else if (this.type === 'birthday') {
                this.local_value = newValue;
                this.validateBirthday(newValue);
            } else {
                this.local_value = newValue;
                this.validateText(newValue);
            }
        },
    },

    mounted() {
        if (this.type === 'tel') {
            const digits = this.getDigitsOnly(this.value);
            this.raw_digits = digits;

            if (digits) {
                if (digits.length <= 11) {
                    this.local_value = this.maskDigits(digits);
                } else {
                    this.local_value = this.value;
                }
                if (this.value) {
                    this.input_touched = true;
                    this.validatePhoneDigits();
                }
            }
        } else if (this.type === 'email') {
            this.local_value = this.value;
            if (this.value) {
                this.input_touched = true;
                this.validateEmail(this.value);
            }
        } else if (this.type === 'official-email') {
            this.local_value = this.value;
            if (this.value) {
                this.input_touched = true;
                this.validateOfficialEmail(this.value);
            }
        } else if (this.type === 'birthday') {
            this.local_value = this.value;
            if (this.value) {
                this.input_touched = true;
                this.validateBirthday(this.value);
            }
        } else if (this.type === 'us-or-ca-tax-id') {
            this.local_value = this.value;
            if (this.value) {
                this.input_touched = true;
                this.validateTaxId(this.value);
            }
        } else {
            this.local_value = this.value;
            if (this.value) {
                this.input_touched = true;
                this.validateText(this.value);
            }
        } 

        if (this.autofocus) {
            this.$nextTick(this.focus);
        }
    },
}
</script>

<style lang="scss" scoped>
// https://accessibleweb.com/color-contrast-checker/
.rc-base-input {
    --text-default: #6b7280;
    --text-dark: #374151;
    --error: #d2233c;
    --dark-grey: #111827;
    --border-color: #c9c9c9;
    --bg-disabled: #f9fafb;
    --primary: var(--v-primary-base, #f24653);
    --color-placeholder: #9ca3af;

    margin-bottom: 1rem;
}

.input-wrapper {
    position: relative;
}

.input-container {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
}

.append-outer-slot {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.input-label {
    display: block;
    font-weight: 500;
    color: var(--text-dark);
    margin-bottom: 0.2rem;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &.underlined {
        margin-bottom: 0;
    }

    &.required {
        .required-indicator {
            color: var(--error);
        }
    }
}

.form-input {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.2;
    color: var(--text-dark);
    background-color: #ffffff;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &.input-underlined {
        border: none;
        border-bottom: 2px solid var(--border-color);
        border-radius: 0;
        padding-left: 0;
        padding-right: 0;
        background-color: transparent;
    }

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &.input-underlined:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }

    &.input-error {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.input-underlined.input-error {
        border-bottom-color: var(--error);

        &:focus {
            border-bottom-color: var(--error);
            box-shadow: none;
        }
    }

    &.input-disabled {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }

    &.input-underlined.input-disabled {
        background-color: transparent;
        border-bottom-color: var(--text-default);
    }

    &.input-readonly {
        background-color: var(--bg-disabled);
    }

    &.input-underlined.input-readonly {
        background-color: transparent;
    }

    &::placeholder {
        color: var(--color-placeholder);
    }

    &.has-prepend-icon {
        padding-left: 2.75rem;
    }
}

.input-container {
    position: relative;

    &.has-icon {
        .input-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-default);
            z-index: 1;
            pointer-events: none;
            transition: color 0.15s ease-in-out;

            &.input-icon-focused {
                color: var(--dark-grey);
            }
        }
    }
}

.error-message {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--error);
    line-height: 1.25rem;
}

.hint-text {
    margin-top: 2px;
    font-size: 0.875rem;
    color: var(--text-default);
    line-height: 1.25rem;
}

@media (prefers-contrast: high) {
    .form-input {
        border-width: 3px;

        &:focus {
            box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.3);
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .form-input {
        transition: none !important;
    }
}
</style>